import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Bot,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Clock3,
  CreditCard,
  Database,
  Menu,
  MessageCircle,
  PlugZap,
  ShieldCheck,
  Sparkles,
  Terminal,
  Workflow,
  X,
} from 'lucide-react';

function BackgroundEffects() {
  return (
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-[#020617]" />
      <div
        className="absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(1200px 500px at 15% -10%, rgba(56,189,248,0.35), transparent 60%), radial-gradient(1100px 480px at 85% -5%, rgba(99,102,241,0.34), transparent 60%), radial-gradient(900px 440px at 50% 115%, rgba(16,185,129,0.28), transparent 60%)',
        }}
      />
      <motion.div
        className="absolute -left-40 top-[-120px] h-[30rem] w-[42rem] rounded-[100%] blur-3xl"
        style={{ background: 'linear-gradient(120deg, rgba(56,189,248,.42), rgba(34,211,238,.08))' }}
        animate={{ x: [0, 120, 0], y: [0, 45, 0], rotate: [0, 7, 0], opacity: [0.55, 0.85, 0.55] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute right-[-240px] top-[-140px] h-[34rem] w-[48rem] rounded-[100%] blur-3xl"
        style={{ background: 'linear-gradient(120deg, rgba(129,140,248,.45), rgba(59,130,246,.1))' }}
        animate={{ x: [0, -140, 0], y: [0, 60, 0], rotate: [0, -10, 0], opacity: [0.5, 0.82, 0.5] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-[-170px] left-[18%] h-[26rem] w-[34rem] rounded-[100%] blur-3xl"
        style={{ background: 'linear-gradient(120deg, rgba(45,212,191,.34), rgba(16,185,129,.08))' }}
        animate={{ x: [0, 90, 0], y: [0, -30, 0], rotate: [0, 6, 0], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-[-200px] right-[6%] h-[24rem] w-[32rem] rounded-[100%] blur-3xl"
        style={{ background: 'linear-gradient(120deg, rgba(244,114,182,.26), rgba(99,102,241,.08))' }}
        animate={{ x: [0, -80, 0], y: [0, -40, 0], rotate: [0, -7, 0], opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
      />
      <div
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage:
            'linear-gradient(rgba(148,163,184,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.14) 1px, transparent 1px)',
          backgroundSize: '40px 40px, 40px 40px',
        }}
      />
    </div>
  );
}

function SectionCard({ icon: Icon, title, text }) {
  return (
    <motion.article
      whileHover={{ y: -6, scale: 1.01 }}
      transition={{ type: 'spring', stiffness: 220, damping: 20 }}
      className="rounded-2xl border border-white/15 bg-white/5 p-5 backdrop-blur-sm"
    >
      <div className="mb-3 inline-flex rounded-lg bg-cyan-300/20 p-2 text-cyan-200">
        <Icon size={18} />
      </div>
      <h3 className="font-heading text-lg text-white">{title}</h3>
      <p className="mt-2 text-sm text-slate-200">{text}</p>
    </motion.article>
  );
}

function LandingPublic() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [signInError, setSignInError] = useState('');
  const demoWidgetRef = useRef(null);

  useEffect(() => {
    if (!signInOpen && !mobileMenuOpen) {
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [signInOpen, mobileMenuOpen]);

  useEffect(() => {
    if (!demoWidgetRef.current || typeof window === 'undefined') return;
    const mount = demoWidgetRef.current;
    mount.innerHTML = '';

    const script = document.createElement('script');
    script.src = `${window.location.origin}/booking-widget.js`;
    script.setAttribute('data-slug', '2c52da935838dd67');
    script.setAttribute('data-api-base', window.location.origin);
    script.setAttribute('data-title', 'Reserva tu cita');
    script.setAttribute('data-subtitle', 'Selecciona servicio y horario');
    script.setAttribute('data-show-reason', 'false');
    script.setAttribute('data-service-required', 'false');
    script.setAttribute('data-reasons', 'Demo,Primera visita,Seguimiento');
    script.setAttribute('data-hide-service', 'true');

    mount.appendChild(script);

    return () => {
      mount.innerHTML = '';
    };
  }, []);

  const navLinks = useMemo(
    () => [
      { label: 'Inicio', href: '#inicio' },
      { label: 'Que es', href: '#que-es' },
      { label: 'OpenClaw', href: '#openclaw' },
      { label: 'Integraciones', href: '#integraciones', withChevron: true },
      { label: 'Contacto', href: '#demo' },
    ],
    []
  );

  const coreFeatures = [
    {
      icon: Calendar,
      title: 'Reservas y agenda inteligente',
      text: 'Centraliza disponibilidad, servicios, recordatorios y reservas online en un solo flujo operativo.',
    },
    {
      icon: MessageCircle,
      title: 'Canales conversacionales',
      text: 'Captura leads desde WhatsApp y web con respuestas asistidas para reducir tiempo de cierre.',
    },
    {
      icon: PlugZap,
      title: 'Formularios para webs',
      text: 'Inserta formularios y widget de reserva en cualquier web para captar oportunidades 24/7.',
    },
    {
      icon: Workflow,
      title: 'Pipelines automatizados',
      text: 'Mueve oportunidades por etapas con reglas, scoring y tareas automáticas para el equipo comercial.',
    },
    {
      icon: ShieldCheck,
      title: 'Control y trazabilidad',
      text: 'Gestiona accesos, auditoria de acciones y reportes para operar con seguridad y claridad.',
    },
    {
      icon: CreditCard,
      title: 'Cobros integrados',
      text: 'Activa pagos online, planes y links de checkout para convertir reservas en ingresos reales.',
    },
    {
      icon: Database,
      title: 'Datos conectados',
      text: 'Sincroniza clientes, calendario y actividad para que ventas, soporte y operaciones trabajen con una sola fuente.',
    },
  ];

  const integrations = [
    'OpenClaw',
    'WhatsApp API',
    'Stripe',
    'NimboxCRM',
    'Mautic',
    'Formularios web (embed)',
    'Google My Business',
    'Webhooks',
    'CalDAV / Radicale',
    'NimboxTalk',
    'APIs internas',
  ];

  const glassStyle = {
    borderColor: 'rgba(164,132,215,0.45)',
    background: 'rgba(49,48,69,0.52)',
  };
  const fadeUp = {
    hidden: { opacity: 0, y: 22 },
    show: { opacity: 1, y: 0 },
  };

  const handleIntegratedSignIn = async (event) => {
    event.preventDefault();
    setSignInError('');
    setIsSigningIn(true);
    try {
      const response = await axios.post('/api/usuarios/login', { email, password });
      const { shortToken, es_admin } = response.data;
      localStorage.setItem('shortToken', shortToken);
      localStorage.setItem('isAdmin', es_admin);
      setSignInOpen(false);
      window.location.href = '/';
    } catch (error) {
      setSignInError(error.response?.data?.error || 'No se pudo iniciar sesion. Verifica tus credenciales.');
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <div className="datacore-landing relative" style={{ background: '#060a14', color: '#f8fafc' }}>
      <section id="inicio" className="relative min-h-screen overflow-hidden">
        <BackgroundEffects />
        <div className="absolute inset-0 bg-black/38" />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(50% 45% at 20% 20%, rgba(34,211,238,0.25) 0%, rgba(0,0,0,0) 100%), radial-gradient(45% 40% at 80% 30%, rgba(249,115,22,0.20) 0%, rgba(0,0,0,0) 100%)',
          }}
        />

        <div className={`relative z-10 mx-auto flex min-h-screen w-full max-w-7xl flex-col px-5 pb-16 pt-6 sm:px-8 lg:px-10 ${signInOpen ? 'pointer-events-none select-none' : ''}`}>
          <header className="flex items-center justify-between gap-4">
            <a href="#inicio" className="flex items-center gap-2">
              <span className="font-heading text-2xl font-bold leading-none text-white">CitasPro</span>
              <span className="font-cabin text-xs uppercase tracking-widest text-slate-300">by Nimbox360</span>
            </a>

            <nav className="hidden items-center gap-7 md:flex">
              {navLinks.map((link) => (
                <a key={link.label} href={link.href} className="font-cabin inline-flex items-center gap-1 text-sm text-slate-200 transition hover:text-white">
                  {link.label}
                  {link.withChevron ? <ChevronDown size={14} /> : null}
                </a>
              ))}
            </nav>

            <div className="hidden items-center gap-3 md:flex">
              <button
                type="button"
                onClick={() => setSignInOpen(true)}
                className="font-cabin rounded-full border px-5 py-2.5 text-sm font-semibold text-white"
                style={glassStyle}
              >
                Sign In
              </button>
              <a href="#demo" className="font-cabin rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ backgroundColor: '#2563eb' }}>
                Agenda Demo
              </a>
            </div>

            <button
              type="button"
              className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/30 bg-white/10 text-white backdrop-blur-sm md:hidden"
              onClick={() => setMobileMenuOpen((value) => !value)}
              aria-label="Open menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </header>

          <main className="mx-auto flex w-full flex-1 items-center justify-center">
            <motion.div
              className="mx-auto max-w-4xl text-center"
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.1 } } }}
            >
              <motion.div variants={fadeUp} transition={{ duration: 0.5 }} className="mx-auto inline-flex items-center gap-3 rounded-full border px-3 py-2 backdrop-blur-md" style={glassStyle}>
                <span className="font-cabin rounded-full bg-orange-400 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-900">Nuevo</span>
                <span className="font-cabin text-sm text-slate-100">OpenClaw + CitasPro v3.2 para equipos comerciales</span>
              </motion.div>

              <motion.h1 variants={fadeUp} transition={{ duration: 0.6 }} className="font-heading mt-8 text-4xl font-semibold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl">
                Convierte conversaciones
                <span className="block">
                  en clientes con una sola <span className="font-serif-accent">plataforma</span>
                </span>
              </motion.h1>

              <motion.p variants={fadeUp} transition={{ duration: 0.6 }} className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-slate-200 sm:text-lg">
                CitasPro organiza tu agenda, lead pipeline, automatizaciones y cobros. Con OpenClaw activas agentes que capturan, califican y mueven oportunidades en tiempo real.
              </motion.p>

              <motion.div variants={fadeUp} transition={{ duration: 0.6 }} className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <motion.a whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} href="#demo" className="font-cabin inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-7 py-3 text-sm font-semibold text-white transition hover:bg-blue-500 sm:w-auto">
                  Reservar Demo
                  <ArrowRight size={16} />
                </motion.a>
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSignInOpen(true)}
                  className="font-cabin inline-flex w-full items-center justify-center rounded-full border border-white/25 bg-white/10 px-7 py-3 text-sm font-semibold text-white transition hover:bg-white/15 sm:w-auto"
                >
                  Entrar al panel
                </motion.button>
              </motion.div>

              <motion.div variants={fadeUp} transition={{ duration: 0.6 }} className="mt-10 grid gap-3 text-sm text-slate-100 sm:grid-cols-3">
                <div className="rounded-xl border border-white/15 bg-black/35 p-3">+32% conversion media en embudos automatizados</div>
                <div className="rounded-xl border border-white/15 bg-black/35 p-3">Respuesta en minutos con agentes OpenClaw</div>
                <div className="rounded-xl border border-white/15 bg-black/35 p-3">Implementacion en dias, no meses</div>
              </motion.div>
            </motion.div>
          </main>
        </div>
      </section>

      <section id="que-es" className="mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 lg:px-10">
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <p className="font-cabin text-sm uppercase tracking-wider text-cyan-300">Que es CitasPro</p>
            <h2 className="font-heading mt-3 text-3xl text-white sm:text-4xl">La capa comercial para equipos que venden por canales digitales</h2>
            <p className="mt-5 text-slate-300">
              No es solo agenda. CitasPro unifica acquisition, seguimiento, conversion y postventa. Con OpenClaw, tus agentes IA operan sobre procesos reales: crear leads,
              agendar citas, actualizar pipeline y activar cobros.
            </p>
            <div className="mt-7 space-y-3">
              {[
                'Leads centralizados con contexto y trazabilidad',
                'Reservas y recordatorios sincronizados',
                'Integracion de formularios para webs',
                'Integracion para Google My Business',
                'Pipeline configurable por negocio',
                'Cobros y planes desde el panel de admin',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 text-slate-100">
                  <CheckCircle2 size={18} className="text-cyan-300" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {coreFeatures.slice(0, 4).map((item) => (
              <SectionCard key={item.title} icon={item.icon} title={item.title} text={item.text} />
            ))}
          </div>
        </div>
      </section>

      <section id="openclaw" className="mx-auto w-full max-w-7xl px-5 pb-20 sm:px-8 lg:px-10">
        <div className="rounded-3xl border border-cyan-300/20 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950/40 p-8 sm:p-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="font-cabin text-sm uppercase tracking-wider text-cyan-300">OpenClaw Workflow</p>
              <h3 className="font-heading mt-3 text-3xl text-white sm:text-4xl">Automatizacion comercial con agentes IA conectados a operaciones</h3>
              <p className="mt-4 text-slate-300">
                Define prompts, permisos y herramientas. OpenClaw ejecuta acciones en CitasPro para que tu equipo mantenga velocidad sin perder control.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200/30 bg-cyan-300/10 px-4 py-2 text-sm text-cyan-100">
              <Sparkles size={16} />
              Produccion ready
            </div>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {[{
              icon: Bot,
              title: '1. Captura y califica',
              text: 'El agente recibe mensajes, detecta intencion y crea el lead con datos estructurados.',
            }, {
              icon: Clock3,
              title: '2. Agenda y seguimiento',
              text: 'Reserva cita en calendario, envia recordatorios y actualiza estado automaticamente.',
            }, {
              icon: PlugZap,
              title: '3. Convierte y sincroniza',
              text: 'Dispara cobro, notifica al equipo y sincroniza con tus integraciones activas.',
            }].map((step) => (
              <SectionCard key={step.title} icon={step.icon} title={step.title} text={step.text} />
            ))}
          </div>
        </div>
      </section>

      <section id="cli" className="mx-auto w-full max-w-7xl px-5 pb-20 sm:px-8 lg:px-10">
        <div className="rounded-3xl border border-blue-300/20 bg-slate-900/70 p-8 backdrop-blur-sm sm:p-10">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="font-cabin text-sm uppercase tracking-wider text-cyan-300">CLI Para Agentes</p>
              <h3 className="font-heading mt-2 text-3xl text-white sm:text-4xl">Controla CitasPro desde OpenClaw con `citaspro-cli`</h3>
              <p className="mt-4 text-slate-300">
                Instala el cliente oficial para ejecutar acciones de negocio desde agentes: generar device code, aprobar acceso, consultar leads y disparar tools.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200/30 bg-cyan-300/10 px-4 py-2 text-sm text-cyan-100">
              <Terminal size={16} />
              Ready for automation
            </div>
          </div>
          <div className="mt-6 rounded-2xl border border-slate-700 bg-slate-950 p-4 font-mono text-sm text-cyan-200">
            npm i -g citaspro-cli
          </div>
        </div>
      </section>

      <section id="integraciones" className="mx-auto w-full max-w-7xl px-5 pb-20 sm:px-8 lg:px-10">
        <div className="mb-8 text-center">
          <p className="font-cabin text-sm uppercase tracking-wider text-cyan-300">Integraciones</p>
          <h3 className="font-heading mt-2 text-3xl text-white sm:text-4xl">Conecta tu stack sin friccion</h3>
          <p className="mx-auto mt-3 max-w-2xl text-slate-300">
            CitasPro se integra con tus canales, pagos y servicios de datos para mantener una operacion comercial coherente.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {integrations.map((name) => (
            <div key={name} className="rounded-xl border border-white/15 bg-white/5 px-4 py-4 text-center text-sm font-medium text-slate-100">
              {name}
            </div>
          ))}
        </div>
      </section>

      <section id="demo" className="mx-auto w-full max-w-7xl px-5 pb-24 sm:px-8 lg:px-10">
        <div className="rounded-3xl border border-blue-300/25 p-8 text-center sm:p-12" style={{ background: 'linear-gradient(130deg, rgba(37,99,235,.20), rgba(14,165,233,.16), rgba(249,115,22,.12))' }}>
          <h3 className="font-heading text-3xl text-white sm:text-4xl">Lleva OpenClaw + CitasPro a tu equipo</h3>
          <p className="mx-auto mt-4 max-w-2xl text-slate-200">
            Te ayudamos a definir el flujo comercial, configurar integraciones y dejar la operacion lista para vender mas con menos friccion.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-white/20 bg-slate-950/80 p-5 text-left">
              <p className="font-cabin text-xs uppercase tracking-wider text-cyan-300">Demo en vivo</p>
              <p className="mt-2 text-sm text-slate-200">
                Prueba el formulario de captación/reserva directamente desde esta landing.
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setSignInOpen(true)}
                  className="font-cabin inline-flex items-center justify-center rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-500"
                >
                  Entrar al panel
                </button>
                <a href="mailto:soporte@citaspro.cloud" className="font-cabin inline-flex items-center justify-center rounded-full border border-white/30 bg-white/10 px-6 py-3 text-sm font-semibold text-white hover:bg-white/15">
                  Contactar ventas
                </a>
              </div>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white p-3">
              <div ref={demoWidgetRef} />
            </div>
          </div>
        </div>
      </section>

      {signInOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              onClick={() => setSignInOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 2147483647,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px',
                backgroundColor: '#020617',
              }}
            >
              <div
                className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl"
                style={{ position: 'relative', zIndex: 2147483647 }}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-heading text-xl text-white">Sign In</h3>
                  <button type="button" onClick={() => setSignInOpen(false)} className="rounded-md p-1 text-slate-300 hover:bg-white/10 hover:text-white">
                    <X size={18} />
                  </button>
                </div>
                <form onSubmit={handleIntegratedSignIn} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Email o username"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="w-full rounded-xl border border-white/20 bg-slate-900 px-4 py-3 text-white placeholder:text-slate-400 focus:border-cyan-300 focus:outline-none"
                    required
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="w-full rounded-xl border border-white/20 bg-slate-900 px-4 py-3 text-white placeholder:text-slate-400 focus:border-cyan-300 focus:outline-none"
                    required
                  />
                  {signInError ? <p className="text-sm text-rose-300">{signInError}</p> : null}
                  <button
                    type="submit"
                    disabled={isSigningIn}
                    className="font-cabin inline-flex w-full items-center justify-center rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSigningIn ? 'Iniciando sesion...' : 'Entrar al panel'}
                  </button>
                </form>
              </div>
            </div>,
            document.body
          )
        : null}

      {mobileMenuOpen && typeof document !== 'undefined'
        ? createPortal(
            <div className="fixed inset-0 z-[2147483600] bg-slate-950 md:hidden">
              <div className="mx-auto flex h-full w-full max-w-md flex-col px-6 pb-8 pt-6">
                <div className="flex items-center justify-between">
                  <a href="#inicio" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                    <span className="font-heading text-3xl font-bold leading-none text-white">CitasPro</span>
                    <span className="font-cabin text-xs uppercase tracking-widest text-slate-300">by Nimbox360</span>
                  </a>
                  <button
                    type="button"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/30 bg-white/10 text-white"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-label="Cerrar menu"
                  >
                    <X size={20} />
                  </button>
                </div>

                <nav className="mt-12 flex flex-col gap-6">
                  {navLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      className="font-cabin text-3xl font-semibold leading-none text-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  ))}
                </nav>

                <div className="mt-auto flex flex-col gap-3">
                  <button
                    type="button"
                    className="font-cabin rounded-full border px-5 py-3 text-center text-sm font-semibold text-white"
                    style={glassStyle}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setSignInOpen(true);
                    }}
                  >
                    Sign In
                  </button>
                  <a
                    className="font-cabin rounded-full bg-blue-600 px-5 py-3 text-center text-sm font-semibold text-white"
                    href="#demo"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Agenda Demo
                  </a>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default LandingPublic;
